<template>
	<div>
		<!-- Member dropdown -->
		<div class="dropdown" v-if="hasToken()">
			<button
				class="btn member-dropdown-toggle d-flex justify-content-between align-items-center"
				type="button"
				id="memberDropdown"
				data-bs-toggle="dropdown"
				:disabled="!isOnline"
				aria-expanded="false">
				<span class="member-name">{{ selectedMember?.name ?? "Myself" }}</span>
				<span class="dropdown-arrow ms-auto">
					<i class="bi bi-caret-down-fill"></i>
				</span>
			</button>
			<!-- Floating label inside the dropdown -->
			<div class="dropdown-menu p-3" style="width: 200px">
				<label for="memberDropdown" class="dropdown-label">View Member</label>
				<!-- <hr /> -->
				<!-- Each person with disconnect icon -->
				<div
					class="dropdown-item d-flex justify-content-between align-items-center">
					<a href="#" @click.prevent="selectMember(null)" class="flex-grow-1"
						>Myself</a
					>
				</div>
				<div class="dropdown-item" v-for="member in members" :key="member.id">
					<a
						href="#"
						@click.prevent="selectMember(member)"
						class="flex-grow-1"
						v-text="member.name"></a>
				</div>
				<hr />
				<!-- Allowed users-->
				<label for="memberDropdown" class="dropdown-label"
					>Allowed Members</label
				>
				<div
					class="dropdown-item d-flex justify-content-between align-items-center"
					v-for="member in allowed"
					:key="member.id">
					<span class="flex-grow-1" v-text="member.name"></span>
					<i
						class="bi bi-x-circle disconnect-icon ms-auto"
						@click.prevent="isOnline && disconnectMember(member)"
						:class="{ disabled: !isOnline }"></i>
				</div>
				<hr />
				<!-- Invite Member section at the bottom -->
				<a
					class="dropdown-item invite-member d-flex align-items-center"
					href="#"
					:class="{ disabled: !isOnline }"
					@click.prevent="isOnline && (showModal = true)">
					<i class="bi bi-person-plus me-2"></i>
					Invite Member
				</a>
			</div>
		</div>
		<!-- End of member dropdown -->

		<!-- Bootstrap Modal -->
		<teleport to="body">
			<div
				v-if="showModal"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog">
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Allow Access</h5>
							<button
								type="button"
								class="btn-close"
								@click="closeModal"></button>
						</div>
						<div class="modal-body">
							<div class="form-group">
								<label for="inviteEmail">Email address of user's account</label>
								<input
									v-model="email"
									type="email"
									id="inviteEmail"
									class="form-control"
									placeholder="Enter member's email address" />
							</div>
						</div>
						<div class="modal-footer d-flex flex-column">
							<button
								type="button"
								class="btn btn-success w-100 mb-2"
								@click="allowAccess">
								Give Access To
							</button>
							<button
								type="button"
								class="btn btn-secondary w-100"
								@click="closeModal">
								Cancel
							</button>
						</div>
					</div>
				</div>
			</div>
			<!-- Modal Backdrop -->
			<div v-if="showModal" class="modal-backdrop fade show"></div>
		</teleport>
	</div>
</template>

<script setup>
	import Swal from "sweetalert2"
	import { nextTick, ref, watch } from "vue"
	import { useTokenStore } from "@/stores/token"
	import { useUserStore } from "@/stores/user"
	// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
	import { fetchWithAuth } from "@/utils/api-client"
	import { isOnline } from "@/utils/networkBus"
	import * as SubscriptionHelper from "@/utils/subscriptionHelper"

	// const props = defineProps("user")

	const tokenStore = useTokenStore()
	const { hasToken } = tokenStore

	const userStore = useUserStore()

	const showModal = ref(false)
	const email = ref("")

	const members = ref([])
	const allowed = ref([])
	const selectedMember = ref(null)

	async function fetchViewUsers() {
		if (hasToken() && isOnline.value) {
			try {
				const response = await fetchWithAuth("/api/access", {
					method: "GET",
					// headers: {
					// 	Authorization: `Bearer ${tokenStore.token}`,
					// },
				})
				// if (hasTokenIssue(response)) {
				// 	handleTokenIssue(response)
				// } else {
				const data = await response.json()
				members.value = data.viewUsers
				allowed.value = data.allowedUsers
				// }
			} catch (error) {
				console.error("Error:", error)
			}
		}
	}

	// Function to handle selecting a member
	async function selectMember(member) {
		if (!isOnline.value) return

		const feature = "View another account"

		const subType = userStore?.user?.subscription_type ?? "basic"
		if (subType === "basic" && member) {
			SubscriptionHelper.showLockedModal(feature)
			return
		} else if (subType === "basic" && !member) {
			return
		}

		try {
			const response = await fetchWithAuth("/api/access/view", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					// Authorization: `Bearer ${tokenStore.token}`,
				},
				body: JSON.stringify({ user_id: member ? member.id : null }),
			})

			if (
				!(await SubscriptionHelper.requestCheckSubscription(response, feature))
			)
				return

			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			const data = await response.json()
			if (data.error) {
				Swal.fire({ icon: "error", title: data.error })
			} else {
				// set new token to token store
				await tokenStore.setTokens(data.access_token, data.refresh_token)
				//force to dashboard
				selectedMember.value = member
				userStore.setActingAsUser(member)
			}
			// }
		} catch (e) {
			console.log(e)
		}
	}

	async function disconnectMember(member) {
		if (!isOnline.value) return
		try {
			// Display SweetAlert confirmation dialog
			const result = await Swal.fire({
				title: `Are you sure you want to disconnect ${member.name}?`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33", // Optional: Customize button colors
				cancelButtonColor: "#3085d6",
				confirmButtonText: "Yes, disconnect",
				cancelButtonText: "Cancel",
				reverseButtons: true,
			})

			if (result.isConfirmed) {
				// User confirmed the action, proceed with disconnecting
				const response = await fetchWithAuth("/api/access/revoke", {
					method: "DELETE",
					headers: {
						"Content-Type": "application/json",
						// Authorization: `Bearer ${tokenStore.token}`,
					},
					body: JSON.stringify({ user_id: member.id }),
				})

				// if (hasTokenIssue(response)) {
				// 	handleTokenIssue(response)
				// } else {
				const data = await response.json()
				if (data.error) {
					Swal.fire({ icon: "error", title: data.error })
				} else {
					// Swal.fire({ icon: "success", title: data.message })
					fetchViewUsers() // Refresh the user list
				}
				// }
			}
			// If the user canceled, do nothing
		} catch (e) {
			console.error("Error disconnecting member:", e)
			Swal.fire({ icon: "error", title: "An unexpected error occurred." })
		}
	}

	// Function to handle sending the invitation
	async function allowAccess() {
		if (!isOnline.value) {
			Swal.fire({
				icon: "error",
				title: "Cannot invite members while offline.",
			})
			return
		}

		const feature = "Invite member to view your account"
		if (SubscriptionHelper.isFreeAccount()) {
			SubscriptionHelper.showLockedModal(feature)
			return
		}

		if (!email.value) {
			Swal.fire({ icon: "error", title: "Email must be filled out." })
		} else {
			try {
				const response = await fetchWithAuth("/api/access/allow", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						// Authorization: `Bearer ${tokenStore.token}`,
					},
					body: JSON.stringify({ email: email.value }),
				})

				if (
					!(await SubscriptionHelper.requestCheckSubscription(
						response,
						feature
					))
				)
					return

				// if (hasTokenIssue(response)) {
				// 	handleTokenIssue(response)
				// } else {
				const data = await response.json()
				if (data.error) {
					Swal.fire({ icon: "error", title: data.error })
				} else {
					Swal.fire({ icon: "success", title: data.message })
					fetchViewUsers()
					closeModal()
				}
				// }
			} catch (e) {
				console.log(e)
			}
			// console.log(`Access sent to: ${email.value}`)
		}
	}

	// Function to close the modal
	function closeModal() {
		showModal.value = false
		email.value = ""
	}

	watch(
		() => userStore.uuid,
		(v) => {
			selectedMember.value = userStore.actingAsUser
		},
		{ immediate: true, deep: true }
	)

	watch(
		() => tokenStore.hasToken(),
		(v, ov) => {
			// console.log("access token", v)
			if (v && v != ov) {
				fetchViewUsers()
			}
		},
		{ deep: true }
	)

	fetchViewUsers()
</script>

<style lang="less" scoped>
	@import "../assets/colors.less";

	.member-dropdown-toggle {
		min-width: 180px;
		padding-left: 15px;
		padding-right: 15px;
		border-radius: 8px;
		text-align: left;
		background-color: @color4;
		color: white;
		border: none;
		box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		.member-name {
			flex-grow: 1;
		}

		.dropdown-arrow {
			flex-shrink: 0;
		}

		&:hover {
			background-color: @navbar-highlight-color;
			color: white;
		}
	}

	button.show {
		background-color: @navbar-highlight-color;
		color: white;
	}

	/* Dropdown label styling inside the dropdown menu */
	.dropdown-menu {
		background-color: @color3;
		border-radius: 5px;
		margin-top: 0;
		width: 100%; /* Ensure the dropdown takes the full width of the button */

		.dropdown-label {
			font-size: 12px;
			color: rgba(255, 255, 255, 0.7);
			margin-bottom: 5px;
			display: block;
		}

		.dropdown-item {
			color: white;
			display: flex;
			align-items: center;
			justify-content: space-between;

			&.disabled {
				opacity: 0.6;
				cursor: not-allowed;
			}

			&:hover {
				background-color: @color5;
			}

			a {
				color: white;
				text-decoration: none;
			}

			.disconnect-icon {
				color: rgba(255, 255, 255, 0.7);
				cursor: pointer;
				&:hover {
					color: white;
				}
			}
		}
	}

	/* Adjust modal backdrop for Bootstrap */
	.modal-backdrop {
		z-index: 1050;
	}
</style>
